import React from "react";
import {
	Box,
	Container,
	Typography,
	Grid,
	Paper,
	List,
	ListItem,
	ListItemText,
	Divider,
	Chip,
} from "@mui/material";

const PrivacyPolicy = () => {
	return (
		<Box sx={{ bgcolor: "#F9FAFB", minHeight: "100vh", py: { xs: 6, md: 8 } }}>
			<Container maxWidth="lg">
				<Paper
					elevation={0}
					sx={{
						p: { xs: 3, md: 5 },
						borderRadius: 3,
						mb: 4,
						background:
							"linear-gradient(135deg, rgba(46,91,220,0.08), rgba(10,31,68,0.04))",
					}}
				>
					<Chip label="Privacy Policy" color="primary" sx={{ mb: 2 }} />
					<Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
						Your Data, Your Control
					</Typography>
					<Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
						Effective Date: 9 February 2026
					</Typography>
					<Typography variant="body1" sx={{ maxWidth: 900 }}>
						This Privacy Policy explains how Orucore - CRM tool ("Orucore", "we",
						"our", or "us") collects, uses, stores, and protects your
						information when you use our website, applications, or services (the
						"Service").
					</Typography>
				</Paper>

				<Grid container spacing={3} sx={{ mb: 4 }}>
					{[
						{
							title: "What we collect",
							desc: "Account details, usage analytics, customer records you upload, and support messages.",
						},
						{
							title: "Why we collect",
							desc: "To run Orucore, improve features, secure the platform, and meet legal obligations.",
						},
						{
							title: "Your rights",
							desc: "Access, correct, export, or delete data and withdraw consent where applicable.",
						},
						{
							title: "Security",
							desc: "We apply encryption, access controls, and monitoring to protect data.",
						},
					].map((item) => (
						<Grid item xs={12} md={6} key={item.title}>
							<Paper elevation={0} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
								<Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
									{item.title}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{item.desc}
								</Typography>
							</Paper>
						</Grid>
					))}
				</Grid>

				<Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
					<Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
						Privacy Details
					</Typography>
					<Divider sx={{ mb: 3 }} />

					<Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
						1. Information We Collect
					</Typography>
					<List dense>
						<ListItem>
							<ListItemText
								primary="Account data"
								secondary="Name, email, phone, company details, and login credentials."
							/>
						</ListItem>
						<ListItem>
							<ListItemText
								primary="Usage data"
								secondary="Pages visited, actions taken, device and browser information, IP address, and timestamps."
							/>
						</ListItem>
						<ListItem>
							<ListItemText
								primary="Customer data"
								secondary="Contacts, notes, and CRM records you upload to Orucore."
							/>
						</ListItem>
						<ListItem>
							<ListItemText
								primary="Support data"
								secondary="Information you provide when contacting support or submitting feedback."
							/>
						</ListItem>
					</List>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						2. Why We Collect Data
					</Typography>
					<List dense>
						<ListItem>
							<ListItemText primary="Provide and operate the Service." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Improve performance, reliability, and user experience." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Communicate product updates, security alerts, and support." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Comply with legal and regulatory obligations." />
						</ListItem>
					</List>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						3. How We Use Your Data
					</Typography>
					<List dense>
						<ListItem>
							<ListItemText primary="Authenticate users and secure accounts." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Process subscriptions and billing." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Analyze usage to enhance features and usability." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Prevent fraud, abuse, or unauthorized access." />
						</ListItem>
					</List>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						4. Cookies & Tracking
					</Typography>
					<Typography variant="body2" color="text.secondary">
						We use cookies and similar technologies to keep you signed in,
						remember preferences, and understand usage patterns. You can disable
						cookies in your browser, but some features may not work correctly.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						5. Data Sharing
					</Typography>
					<Typography variant="body2" color="text.secondary">
						We do not sell your personal data. We may share data with trusted
						service providers who help us operate Orucore (hosting, analytics,
						payments), under strict confidentiality and security obligations.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						6. Data Storage & Security
					</Typography>
					<Typography variant="body2" color="text.secondary">
						We store data in secure environments and use industry-standard
						safeguards (encryption, access controls, and monitoring) to protect
						your information. No method of transmission or storage is 100%
						secure, but we work to protect your data responsibly.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						7. Data Retention
					</Typography>
					<Typography variant="body2" color="text.secondary">
						We retain your data for as long as your account is active or as
						needed to provide the Service and comply with legal obligations. You
						can request deletion at any time.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						8. Your Rights
					</Typography>
					<List dense>
						<ListItem>
							<ListItemText primary="Access, correct, or delete your personal data." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Export your data where applicable." />
						</ListItem>
						<ListItem>
							<ListItemText primary="Withdraw consent for certain processing." />
						</ListItem>
					</List>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						9. Compliance
					</Typography>
					<Typography variant="body2" color="text.secondary">
						We strive to comply with applicable data protection regulations such
						as GDPR and CCPA where relevant.
					</Typography>

					<Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
						10. Contact Us
					</Typography>
					<Typography variant="body2" color="text.secondary">
						For privacy questions or data requests, contact us at
						<strong> support@orucore.com</strong>.
					</Typography>
				</Paper>
			</Container>
		</Box>
	);
};

export default PrivacyPolicy;
